<template>
  <q-card flat bordered>
    <q-skeleton v-show="loading" class="google-map" animation="blink" />
    <google-map
      v-show="!loading"
      ref="mapRef"
      data-private
      :api-promise="apiPromise"
      class="google-map"
      :api-key="apiKey"
      :max-zoom="16"
      :styles="theme"
    >
      <google-marker
        v-for="(prop, idx) in allProperties"
        :key="prop.id"
        :options="{
          position: { lat: prop.lat, lng: prop.lon },
          optimized: true,
          icon: {
            url: subjectDetail?.id === prop.id ? '/marker-primary.svg' : '/marker-accent.svg',
            labelOrigin: { x: 16, y: 17 }
          },
          label: {
            text: subjectDetail?.id === prop.id ? '★' : (idx + (subjectVisible ? 0 : 1)).toString(),
            color: subjectDetail?.id === prop.id ? '#6365f1' : '#FF8800',
            fontWeight: 'bold'
          },
          zIndex: allProperties.length - idx
        }"
        @click="openMarker"
      >
        <info-window ref="InfoWindowTest" :options="{ headerDisabled: true }" class="text-black">
          <div class="row items-center">
            <div v-if="!errorLoadingImage[prop.id]" class="q-mr-md">
              <q-img
                data-private
                :src="propertyThumbnail(prop)"
                style="width: 165px"
                alt="Building Image"
                class="rounded-borders"
                @error="errorLoadingImage[prop.id] = true"
                crossorigin="anonymous"
              />
            </div>
            <div>
              <div class="row q-col-gutter-sm justify-between">
                <div>
                  <div class="row items-center">
                    <div
                      class="q-mb-xs ellipsis"
                      style="font-size: 1rem; font-weight: 500; max-width: 300px"
                    >
                      {{ prop.building_name }}
                    </div>
                    <new-property-report-icon :property-id="prop.id" />
                  </div>
                  <div class="ellipsis" style="max-width: 300px; font-size: 0.8rem">
                    {{ prop.street_address }}, {{ prop.city }}, {{ prop.state }},
                    {{ prop.zip_code }}
                  </div>
                </div>
                <div>
                  <!-- close the popup with a X icon, use bold for the icon -->
                  <q-icon
                    name="close"
                    class="cursor-pointer font-bold"
                    size="17px"
                    @click="closeInfoWindow"
                  />
                </div>
              </div>
              <div class="row q-col-gutter-xs q-col-gutter-y-sm" style="margin-top: 1px">
                <div v-if="similarities[prop.id]?.overall" class="col-4">
                  <div class="metric-label">Similarity</div>
                  <q-icon name="data_usage" />
                  {{ formatPercent(similarities[prop.id].overall, 0) }}
                </div>
                <div v-if="similarities[prop.id]?.distance_miles" class="col-4">
                  <div class="metric-label">Distance</div>
                  <q-icon name="place" />
                  {{ similarities[prop.id].distance_miles.toFixed(2) }} mi
                </div>
                <div class="col-4">
                  <div class="metric-label"># Units</div>
                  <q-icon name="apartment" />
                  {{ prop.number_units || '-' }}
                </div>
                <div class="col-4">
                  <div class="metric-label"># Stories</div>
                  <q-icon name="layers" />
                  {{ prop.number_stories || '-' }}
                </div>
                <div class="col-4">
                  <div class="metric-label">Year Built</div>
                  <q-icon name="date_range" />
                  {{ prop.year_built || '-' }}
                </div>
                <div v-if="!deleteEnabled" class="col-4">
                  <div class="metric-label">Asset Class</div>
                  <q-icon name="grade" />
                  {{
                    prop.building_quality.property_overall_quality
                      ? humanizeQualityScore(prop.building_quality.property_overall_quality)
                      : '-'
                  }}
                </div>
                <div v-if="deleteEnabled" class="col-4">
                  <q-btn
                    no-caps
                    icon="delete_outline"
                    class="full-width"
                    unelevated
                    color="red"
                    size="sm"
                    outline
                    @click="deleteAddress(prop.id)"
                  >
                    Delete
                  </q-btn>
                </div>
              </div>
            </div>
          </div>
        </info-window>
      </google-marker>
    </google-map>
  </q-card>
</template>

<script lang="ts">
import { type PropType, watch } from 'vue'
import {
  getStreetViewUrl,
  humanizeQualityScore,
  type Availability,
  type AvailabilityPeriod,
  type Price,
  type PropertyDetailsResponse,
  type Similarities
} from 'common'
import { GoogleMap, Marker as GoogleMarker, InfoWindow } from 'vue3-google-map'
import { formatPercent, formatQuality, lightMapTheme, darkMapTheme } from '@/utils'
import apiPromise from '@/setups/googleMaps'
import NewPropertyReportIcon from '@/components/NewPropertyReportIcon.vue'

export default {
  name: 'PropertyCompareGoogleMap',
  components: { NewPropertyReportIcon, GoogleMap, GoogleMarker, InfoWindow },
  props: {
    subjectDetail: {
      type: Object as PropType<PropertyDetailsResponse | null>,
      default: null
    },
    filteredProperties: {
      type: Array<{
        prop: PropertyDetailsResponse
        units: {
          unit: Availability
          lastHistory: Price
          period: AvailabilityPeriod
        }[]
      }>,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    deleteEnabled: {
      type: Boolean,
      default: false
    },
    similarities: {
      type: Object as PropType<{ [key: string]: Similarities }>,
      default: () => ({})
    }
  },
  emits: ['delete-address'],
  data() {
    return {
      apiPromise,
      openedMarkerID: null as null | string,
      apiKey: import.meta.env.VITE_GOOGLE_API_KEY,
      mapRef: null as null | typeof GoogleMap,
      errorLoadingImage: {} as Record<string, boolean>
    }
  },
  computed: {
    subjectVisible() {
      return this.filteredProperties.some((_) => _.prop.id === this.subjectDetail?.id)
    },
    theme() {
      return this.$q.dark.isActive ? darkMapTheme : lightMapTheme
    },
    allProperties() {
      return this.filteredProperties.map((_) => _.prop)
    }
  },
  watch: {
    allProperties() {
      this.onLoad()
    }
  },
  mounted() {
    this.mapRef = this.$refs['mapRef'] as typeof GoogleMap
    watch(() => this.mapRef?.ready, this.onLoad)
  },
  methods: {
    humanizeQualityScore,
    formatPercent,
    formatQuality,
    closeInfoWindow() {
      const infoWindows = this.$refs['InfoWindowTest'] as (typeof InfoWindow)[]
      for (const infoWindow of infoWindows) {
        infoWindow.close()
      }
    },
    openMarker() {
      this.closeInfoWindow()
    },
    propertyThumbnail(prop: PropertyDetailsResponse) {
      return prop.street_view_url || getStreetViewUrl(prop, import.meta.env.VITE_API_URL)
    },
    deleteAddress(rowId: string) {
      this.$emit('delete-address', rowId)
    },
    onLoad() {
      if (this.mapRef === null) {
        console.warn('mapRef is null')
      } else if (this.mapRef.ready) {
        const bounds = new google.maps.LatLngBounds()
        this.allProperties.forEach((d) => {
          bounds.extend(new google.maps.LatLng(d.lat, d.lon))
        })
        const mapRefVal = this.$refs.mapRef as typeof GoogleMap
        const gmap = mapRefVal.map
        gmap.fitBounds(bounds)
      }
    }
  }
}
</script>
<style scoped lang="scss">
.google-map {
  width: 100%;
  min-height: 300px;
  height: calc(100vh / 3);
}
.metric-label {
  font-size: 0.8em;
  font-weight: 400;
  color: #6c757d;
}
</style>
